import { faList, faListCheck, faPlus } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate } from '@tanstack/react-router'
import type { ReactElement } from 'react'
import { useContext, useMemo, useState } from 'react'
import { TypeFilter } from '~/components/Filters/TypeFilter'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { useConfig } from '~/domains/configuration/queries/config'
import { useDashboardContext } from '~/domains/dashboard/context/DashboardContext'
import type { DashboardBlockLayoutProps } from '~/domains/dashboard/types'
import { TaskAction } from '~/domains/tasks/components/TaskAction/TaskAction'
import { TaskCard } from '~/domains/tasks/components/TaskCard/TaskCard'
import { taskGrid } from '~/domains/tasks/components/TaskCard/TaskCard.css'
import { useTasks } from '~/domains/tasks/queries/tasks'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import { AsideActionContext } from '~/utils/contextes'
import { DashboardBlockLayout } from '../../DashboardBlock/DashboardBlockLayout'
import { actionsListFooter, actionsListLoader } from './NextActionsBlock.css'
import { NextActionsConfigDropdown } from './NextActionsConfigDropdown'

const baseTypeFilterOptions = [
  { label: 'Appels', value: 'call' },
  { label: 'Rendez-vous', value: 'meeting' },
  { label: 'Tâches libres', value: 'task' }
]

export const NextActionsBlock = (
  props: DashboardBlockLayoutProps
): ReactElement => {
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set())
  const { userIds, isUserIdsLoading } = useDashboardContext()
  const { setAsideAction } = useContext(AsideActionContext)
  const navigate = useNavigate()
  const { data: actionTypes } = useTaxonomy('action_types')

  const { data: todayBefore } = useConfig('dashboard.next_actions_today_before')
  const { data: todayAfter } = useConfig('dashboard.next_actions_today_after')

  const typeFilterOptions = useMemo(
    () => [
      ...baseTypeFilterOptions,
      ...(actionTypes ?? [])
        .filter((type) => type.expectsCompletion)
        .map((type) => ({ label: type.name, value: String(type.id) }))
    ],
    [actionTypes]
  )

  const scheduledDateParams = useMemo(() => {
    const fromDays = todayBefore
    const toDays = todayAfter

    const params: { after?: string; before?: string } = {}

    if (fromDays !== null && fromDays !== undefined && fromDays > 0) {
      const fromDate = new Date()
      try {
        fromDate.setDate(fromDate.getDate() - Number(fromDays))
        params.after = fromDate.toISOString()
      } catch (error) {
        console.error(error)
      }
    } else {
      params.after = new Date().toISOString()
    }

    if (toDays !== null && toDays !== undefined && toDays > 0) {
      const toDate = new Date()
      try {
        toDate.setDate(toDate.getDate() + Number(toDays))
        params.before = toDate.toISOString()
      } catch (error) {
        console.error(error)
      }
    }

    return params
  }, [todayBefore, todayAfter])

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError
  } = useTasks({
    user: userIds,
    types: Array.from(selectedTypes),
    itemsPerPage: 10,
    isCompleted: false,
    scheduledDate: scheduledDateParams,
    enabled:
      userIds.length > 0 &&
      todayBefore !== undefined &&
      todayAfter !== undefined &&
      !isUserIdsLoading
  })

  const allTasks = useMemo(() => {
    if (!data?.pages) return []

    return data.pages.flatMap((page) => page['hydra:member'])
  }, [data])

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  const actions = (
    <div data-onboarding="next-actions-config">
      <ButtonGroup>
        <Button
          variation={ButtonVariation.Neutral}
          buttonSize="extraSmall"
          onClick={() => navigate({ to: '/tasks' })}
        >
          <FontAwesomeIcon icon={faList} />
          Voir toutes les tâches
        </Button>
        <Tooltip text="Nouvelle tâche" direction="top" asChild>
          <Button
            variation={ButtonVariation.Neutral}
            buttonSize="extraSmall"
            narrow
            onClick={() => setAsideAction({ action: <TaskAction /> })}
          >
            <FontAwesomeIcon icon={faPlus} />
          </Button>
        </Tooltip>
        {todayBefore !== undefined && todayAfter !== undefined && (
          <NextActionsConfigDropdown />
        )}
        <TypeFilter
          options={typeFilterOptions}
          selectedTypes={selectedTypes}
          onTypesChange={(types) => setSelectedTypes(new Set(types))}
        />
      </ButtonGroup>
    </div>
  )

  if (isError) {
    return (
      <DashboardBlockLayout {...props}>
        <Alert
          variation={AlertVariation.Danger}
          message="Erreur lors du chargement des tâches"
          onDismiss={false}
        />
      </DashboardBlockLayout>
    )
  }

  if (isLoading || isUserIdsLoading) {
    return (
      <DashboardBlockLayout {...props} actions={actions}>
        <div className={actionsListLoader}>
          <Shimmer width="100%" height={50} />
          <Shimmer width="100%" height={50} />
          <Shimmer width="100%" height={50} />
        </div>
      </DashboardBlockLayout>
    )
  }

  if (allTasks.length === 0) {
    return (
      <DashboardBlockLayout {...props} actions={actions}>
        <NoResult icon={faListCheck} message="Aucune tâche prévue" isSmall />
      </DashboardBlockLayout>
    )
  }

  return (
    <DashboardBlockLayout {...props} actions={actions}>
      <div className={taskGrid}>
        {allTasks.map((task) => (
          <TaskCard key={task.id} task={task} />
        ))}

        {hasNextPage && (
          <div className={actionsListFooter}>
            <ButtonGroup align="center">
              <Button
                variation={ButtonVariation.Neutral}
                onClick={handleLoadMore}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage
                  ? 'Chargement...'
                  : 'Charger plus de tâches'}
              </Button>
            </ButtonGroup>
          </div>
        )}
      </div>
    </DashboardBlockLayout>
  )
}
